import { useEffect, useState } from "react";
import { Palette } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import {
  APP_BACKGROUND_INTENSITIES,
  APP_BACKGROUND_INTENSITY_IDS,
  APP_BACKGROUND_PALETTE_IDS,
  APP_BACKGROUND_PALETTES,
  APP_BACKGROUND_TEXTURE_IDS,
  APP_BACKGROUND_TEXTURES,
  APP_BACKGROUND_THEME_IDS,
  APP_BACKGROUND_THEMES,
  DEFAULT_APP_BACKGROUND_CUSTOM,
  normalizeAppBackgroundCustom,
  normalizeAppBackgroundTheme,
} from "@/lib/appBackgroundThemes";
import {
  updateAppBackgroundThemePreference,
  useUiPreferences,
} from "@/lib/useUiPreferences";

const PRESET_IDS = APP_BACKGROUND_THEME_IDS.filter((id) => id !== "custom");

/**
 * Selector de fondo decorativo — solo diseñador BNT (embebido en hub / herramientas).
 * Pestañas: Presets | Personalizado (paleta + textura + intensidad).
 */
export default function AppBackgroundThemePanel() {
  const { appBackgroundTheme, appBackgroundCustom, loading } = useUiPreferences();
  const [draftTheme, setDraftTheme] = useState(appBackgroundTheme);
  const [draftCustom, setDraftCustom] = useState(appBackgroundCustom);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(appBackgroundTheme === "custom" ? "personalizado" : "presets");

  useEffect(() => {
    setDraftTheme(appBackgroundTheme);
    setDraftCustom(appBackgroundCustom);
    setTab(appBackgroundTheme === "custom" ? "personalizado" : "presets");
  }, [appBackgroundTheme, appBackgroundCustom]);

  const persist = async (themeId, customOverride = null) => {
    const nextTheme = normalizeAppBackgroundTheme(themeId);
    const nextCustom =
      customOverride != null
        ? normalizeAppBackgroundCustom(customOverride)
        : normalizeAppBackgroundCustom(draftCustom);
    setDraftTheme(nextTheme);
    if (customOverride != null) setDraftCustom(nextCustom);
    setSaving(true);
    try {
      await updateAppBackgroundThemePreference(nextTheme, nextCustom);
      if (nextTheme === "custom") {
        const pal = APP_BACKGROUND_PALETTES[nextCustom.palette];
        const tex = APP_BACKGROUND_TEXTURES[nextCustom.texture];
        toast.success(
          `Diseño propio: ${pal?.label || nextCustom.palette} · ${tex?.label || nextCustom.texture}`,
        );
      } else {
        const meta = APP_BACKGROUND_THEMES[nextTheme];
        toast.success(meta ? `Fondo: ${meta.label}` : "Preferencia de fondo guardada");
      }
    } catch (e) {
      setDraftTheme(appBackgroundTheme);
      setDraftCustom(appBackgroundCustom);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar el fondo");
    } finally {
      setSaving(false);
    }
  };

  const onSelectPreset = (themeId) => persist(themeId);

  const onSelectPalette = (paletteId) => {
    const next = { ...draftCustom, palette: paletteId };
    setDraftCustom(normalizeAppBackgroundCustom(next));
    persist("custom", next);
  };

  const onSelectTexture = (textureId) => {
    const next = { ...draftCustom, texture: textureId };
    setDraftCustom(normalizeAppBackgroundCustom(next));
    persist("custom", next);
  };

  const onSelectIntensity = (intensityId) => {
    const next = { ...draftCustom, intensity: intensityId };
    setDraftCustom(normalizeAppBackgroundCustom(next));
    persist("custom", next);
  };

  const onActivateCustomTab = (value) => {
    setTab(value);
    if (value === "personalizado" && draftTheme !== "custom") {
      persist("custom", draftCustom || DEFAULT_APP_BACKGROUND_CUSTOM);
    }
  };

  return (
    <div
      className="rounded-md border border-slate-200 bg-white shadow-sm p-4 space-y-4"
      data-testid="app-background-theme-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <Palette className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#0F2D5C]">Apariencia del sistema</Label>
          <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
            Capa decorativa sutil detrás del contenido. No afecta permisos, datos ni flujos. Ideal para
            dar un toque de despacho contable sin saturar tablas ni formularios.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={onActivateCustomTab}>
        <TabsList className="h-9 bg-slate-100" data-testid="app-background-tabs">
          <TabsTrigger value="presets" data-testid="app-background-tab-presets">
            Presets
          </TabsTrigger>
          <TabsTrigger value="personalizado" data-testid="app-background-tab-personalizado">
            Personalizado
          </TabsTrigger>
        </TabsList>

        <TabsContent value="presets" className="mt-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Tema de fondo">
            {PRESET_IDS.map((id) => {
              const meta = APP_BACKGROUND_THEMES[id];
              const selected = draftTheme === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={loading || saving}
                  onClick={() => onSelectPreset(id)}
                  data-testid={`app-background-theme-${id}`}
                  className={`text-left rounded-sm border px-3 py-2.5 transition-colors ${
                    selected
                      ? "border-[#0F2D5C] bg-[#0F2D5C]/[0.06] ring-1 ring-[#0F2D5C]/30"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <p className="text-sm font-medium text-[#0F2D5C]">{meta.label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{meta.description}</p>
                </button>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="personalizado" className="mt-3 space-y-4">
          <div>
            <p className="text-xs font-medium text-[#0F2D5C] mb-2">Paleta de colores</p>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
              role="radiogroup"
              aria-label="Paleta de fondo"
            >
              {APP_BACKGROUND_PALETTE_IDS.map((id) => {
                const meta = APP_BACKGROUND_PALETTES[id];
                const selected = draftTheme === "custom" && draftCustom.palette === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={loading || saving}
                    onClick={() => onSelectPalette(id)}
                    data-testid={`app-background-palette-${id}`}
                    className={`text-left rounded-sm border px-3 py-2.5 transition-colors ${
                      selected
                        ? "border-[#0F2D5C] bg-[#0F2D5C]/[0.06] ring-1 ring-[#0F2D5C]/30"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className="inline-block w-4 h-4 rounded-sm border border-slate-200 shrink-0"
                        style={{ background: meta.primary }}
                        aria-hidden
                      />
                      <span
                        className="inline-block w-4 h-4 rounded-sm border border-slate-200 shrink-0"
                        style={{ background: meta.secondary }}
                        aria-hidden
                      />
                      <span
                        className="inline-block w-4 h-4 rounded-sm border border-slate-200 shrink-0"
                        style={{ background: meta.wash }}
                        aria-hidden
                      />
                    </div>
                    <p className="text-sm font-medium text-[#0F2D5C]">{meta.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-[#0F2D5C] mb-2">Textura</p>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              role="radiogroup"
              aria-label="Textura de fondo"
            >
              {APP_BACKGROUND_TEXTURE_IDS.map((id) => {
                const meta = APP_BACKGROUND_TEXTURES[id];
                const selected = draftTheme === "custom" && draftCustom.texture === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={loading || saving}
                    onClick={() => onSelectTexture(id)}
                    data-testid={`app-background-texture-${id}`}
                    className={`text-left rounded-sm border px-3 py-2.5 transition-colors ${
                      selected
                        ? "border-[#0F2D5C] bg-[#0F2D5C]/[0.06] ring-1 ring-[#0F2D5C]/30"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <p className="text-sm font-medium text-[#0F2D5C]">{meta.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-[#0F2D5C] mb-2">Intensidad</p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Intensidad del fondo">
              {APP_BACKGROUND_INTENSITY_IDS.map((id) => {
                const meta = APP_BACKGROUND_INTENSITIES[id];
                const selected = draftTheme === "custom" && draftCustom.intensity === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={loading || saving}
                    onClick={() => onSelectIntensity(id)}
                    data-testid={`app-background-intensity-${id}`}
                    className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? "border-[#0F2D5C] bg-[#0F2D5C]/[0.06] text-[#0F2D5C] font-medium ring-1 ring-[#0F2D5C]/30"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
